import G6 from '@antv/g6'
import { getCssColorVar } from '@/utils/chart-theme-colors'
import { getSvgIconDataUrl } from '@/utils/svg-icon-data-url'
import { isSelfOrChild, TeamItem } from '@/views/dashboard/cascade-center/branch-overview/branch-overview.service'
import { overflowText } from '@/utils/format'
import { team as teamService } from '@/common/basis/root-injector'
import TreeGraph from '@antv/g6-pc/lib/graph/tree-graph'

const TEAM_NAME_SHOW_LENGTH = 9
const CM_COLOR_PRIMARY = 'var(--cm-color-primary)'

/** 获取主题主色实际值（从 CSS 变量解析） */
function getThemePrimaryColor (): string {
  if (typeof document === 'undefined') return '#409eff' /* dm-keep：CSS 变量读取失败时的品牌蓝兜底 */
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--cm-color-primary')
    .trim() || '#409eff' /* dm-keep：CSS 变量读取失败时的品牌蓝兜底 */
}

/** fitView 后再缩放；默认 0.9 略缩小视图 */
export const CASCADE_CHART_DEFAULT_ZOOM_RATIO: number = 1

/** 先 fitView；比例非 1 时在视口中心缩放 */
export function fitViewWithDefaultZoom(graph: TreeGraph, animate = false) {
  graph.fitView(undefined, undefined, animate)
  if (Math.abs(CASCADE_CHART_DEFAULT_ZOOM_RATIO - 1) > 1e-6) {
    graph.zoom(CASCADE_CHART_DEFAULT_ZOOM_RATIO, graph.getViewPortCenterPoint(), animate)
  }
}

let teamTreeData = {} as TeamItem

/** 节点尺寸 */
const NODE_RECT = {
  width: 180,
  height: 82,
}

/** 节点左上角坐标（原点） */
const NODE_ORIGIN = {
  x: -NODE_RECT.width / 2,
  y: -NODE_RECT.height / 2,
}

/** 节点默认样式（函数形式：颜色运行时读取 CSS 变量，主题切换后重建图生效） */
const getDefaultNodeStyle = () => ({
  fill: getCssColorVar('--cm-color-card-bg', '#FAFAFA'),
  stroke: getCssColorVar('--cm-color-border-lighter', '#E7E7E7'),
  radius: 4,
})

const getDefaultEdgeStyle = () => ({
  stroke: CM_COLOR_PRIMARY,
  lineWidth: 1,
  endArrow: {
    path: 'M 0, 0 L -6, 4 L 0, 0 L -6, -4 Z',
    fill: CM_COLOR_PRIMARY,
    d: NODE_RECT.height / 2,
  },
})

const defaultLayout = {
  type: 'compactBox',
  direction: 'TB',

  /** 不能删除，布局内部给节点打标识用的函数，必须返回一个唯一的字符串或数字，默认按 data.id。 */
  getId: (d: TeamItem) => {
    return d.id
  },
  /** 不能删除，告诉布局引擎每个节点的包围盒尺寸，用来做碰撞检测和排布。 */
  getHeight: (d) => {
    return NODE_RECT.height
  },
  /** 不能删除，告诉布局引擎每个节点的包围盒尺寸，用来做碰撞检测和排布。 */
  getWidth: (d) => {
    return NODE_RECT.width
  },
  /** 不能删除，如果存在 getVGap，则 垂直间距 用它；否则退回到 levelDistance。 */
  getVGap: (d) => {
    return 33
  },
  /** 不能删除，如果存在 getHGap，则 水平间距 用它；否则退回到 distance。 */
  getHGap: (d) => {
    return 12
  },
}

/** 节点悬停与选中样式（函数形式：颜色运行时读取 CSS 变量，主题切换后重建图生效） */
const getNodeLightStyle = () => ({
  stroke: CM_COLOR_PRIMARY,
  lineDash: [2, 2], // 虚线
  lineWidth: 1,
  shadowColor: 'transparent',
  fill: getCssColorVar('--cm-color-active-bg', '#F1F5FF'),
  'separator-line': {
    stroke: getCssColorVar('--el-color-primary-light-5', '#C2D2FF'),
  },
  'terminal-count-label': {
    fill: CM_COLOR_PRIMARY,
  },
  'terminal-count-value': {
    fill: CM_COLOR_PRIMARY,
  }
})

const getNodeStateStyles = () => ({
  hover: {}, // 只要有hover状态逻辑这个就不能删，不然选中时悬停边框高亮会消失--G6bug？
  selected: getNodeLightStyle()
})

G6.registerNode(
  'icon-node',
  {
    draw(cfg: TeamItem, group) {
      const styles = this.getShapeStyle(cfg)
      const hasInfo = isSelfOrChild(teamTreeData, +teamService.getCurrentTeam()?.team_id, +cfg.team_id)
      const keyShape = group.addShape('rect', {
        attrs: {
          ...styles,
          x: NODE_ORIGIN.x,
          y: NODE_ORIGIN.y,
        },
        name: 'key-shape',
      })

      const iconSize = 16
      const iconTop = 16
      // team-icon
      group.addShape('image', {
        attrs: {
          x: NODE_ORIGIN.x + 16,
          y: NODE_ORIGIN.y + iconTop,
          width: iconSize,
          height: iconSize,
          img: getSvgIconDataUrl(cfg.name_icon as string),
        },
        name: 'team-icon',
      })

      // team-name（与 team-icon 垂直居中）
      const titleRowCenterY = NODE_ORIGIN.y + iconTop + iconSize / 2
      group.addShape('text', {
        attrs: {
          text: overflowText(cfg.name, TEAM_NAME_SHOW_LENGTH),
          fontSize: 14,
          fontWeight: 'bold',
          fill: +cfg.online === 2
            ? getCssColorVar('--cm-color-text-secondary', '#83899D')
            : getCssColorVar('--cm-color-text-primary', '#000000'),
          x: NODE_ORIGIN.x + 16 + iconSize + 8,
          y: titleRowCenterY,
          textBaseline: 'middle',
        },
        name: 'team-name',
      })

      if (cfg.team_id === teamService.getCurrentTeam()?.team_id) {
        const locSize = 14
        // location-icon
        group.addShape('image', {
          attrs: {
            x: NODE_ORIGIN.x + NODE_RECT.width - 29,
            y: titleRowCenterY - locSize / 2,
            width: locSize,
            height: locSize,
            img: getSvgIconDataUrl('views/dashboard/cascade-center/branch-overview/_images/location'),
          },
          name: 'location-icon',
        })
      }

      // separator-line
      group.addShape('line', {
        attrs: {
          x1: NODE_ORIGIN.x + 17,
          x2: NODE_ORIGIN.x + NODE_RECT.width - 17,
          y1: NODE_ORIGIN.y + 48,
          y2: NODE_ORIGIN.y + 48,
          stroke: getCssColorVar('--cm-color-border-lighter', '#D9D9D9'),
          lineWidth: 1,
        },
        name: 'separator-line', // 命名元素，方便后续操作
      })

      // terminal-count-label
      group.addShape('text', {
        attrs: {
          text: '终端总数',
          fontSize: 14,
          fill: getCssColorVar('--cm-color-text-secondary', '#83899D'),
          x: NODE_ORIGIN.x + 16,
          y: NODE_ORIGIN.y + 75,
        },
        name: 'terminal-count-label',
      })

      // terminal-count-value
      group.addShape('text', {
        attrs: {
          text: hasInfo ? cfg.count : '***',
          fontSize: 14,
          fill: getCssColorVar('--cm-color-text-secondary', '#83899D'),
          x: NODE_ORIGIN.x + 74,
          y: NODE_ORIGIN.y + (hasInfo ? 75 : 78),
        },
        name: 'terminal-count-value',
      })

      return keyShape
    },
    /** 选中态：图标颜色切换为主题色 */
    setState (name, value, item) {
      if (name !== 'selected') return
      const group = item.getContainer()
      const teamIcon = group.find((el: any) => el.get('name') === 'team-icon')
      const locationIcon = group.find((el: any) => el.get('name') === 'location-icon')

      const themeColor = value ? getThemePrimaryColor() : ''

      if (teamIcon) {
        teamIcon.attr('color', themeColor)
      }
      if (locationIcon) {
        locationIcon.attr('color', themeColor)
      }
    },
  } as any,
  'rect',
)

G6.registerEdge('flow-line', {
  draw(cfg, group) {
    const startPoint = cfg.startPoint
    const endPoint = cfg.endPoint
    const { style } = cfg

    const path = [
      ['M', startPoint.x, startPoint.y],
      ['L', startPoint.x, (startPoint.y + endPoint.y) / 2],
      ['L', endPoint.x, (startPoint.y + endPoint.y) / 2],
      ['L', endPoint.x, endPoint.y],
    ]

    const shape = group.addShape('path', {
      attrs: {
        stroke: style.stroke,
        endArrow: style.endArrow,
        lineWidth: style.lineWidth,
        path,
      },
    })

    return shape
  },
})

const tooltip = new G6.Tooltip({
  itemTypes: ['node'],
  getContent: (e) => {
    const outDiv = document.createElement('div')
    const model = e.item.getModel() as TeamItem
    if (e?.shape?.cfg?.name === 'team-name') {
      outDiv.innerHTML = model.name
    } else if (e?.shape?.cfg?.name === 'team-icon') {
      outDiv.innerHTML = '该控制中心已离线'
    }
    return outDiv
  },
  shouldBegin: (e) => {
    const model = e?.item?.getModel() as TeamItem
    const showNameOverflow = e?.shape?.cfg?.name === 'team-name' && model?.name?.length > TEAM_NAME_SHOW_LENGTH
    const showOutline = e?.shape?.cfg?.name === 'team-icon' && model?.online === 2
    return showNameOverflow || showOutline
  }
})

function bindEvents (graph: TreeGraph) {
  graph.on('node:mouseover', (evt) => {
    const { item } = evt
    graph.setItemState(item, 'hover', true)
  })
  graph.on('node:mouseout', (evt) => {
    const { item } = evt
    graph.setItemState(item, 'hover', false)
  })
}

export function initChart(container: HTMLElement, data: TeamItem) {
  teamTreeData = data
  const graph = new G6.TreeGraph({
    container,
    renderer: 'svg', // 'canvas' image-shape hover后会抖动
    plugins: [tooltip],
    maxZoom: 1.4,
    linkCenter: true,
    fitCenter: true,
    fitViewPadding: [40, 40, 40, 40],
    fitView: false,
    modes: {
      default: ['drag-canvas'],
    },
    defaultNode: {
      type: 'icon-node',
      size: [NODE_RECT.width, NODE_RECT.height],
      style: getDefaultNodeStyle(),
    },
    defaultEdge: {
      type: 'flow-line',
      style: getDefaultEdgeStyle(),
    },
    nodeStateStyles: getNodeStateStyles(),
    layout: defaultLayout,
  })
  bindEvents(graph)
  return graph
}
